import axios from 'axios';
import config from '../config';
import applyTokenRefreshInterceptor from '../utils/tokenRefreshInterceptor';
import applyAuthRetryInterceptor from '../utils/applyAuthRetryInterceptor';

// Axios instance for the Reviews API service
const reviewsClient = axios.create({
  baseURL: config.api.reviewsServiceUrl,
  timeout: 30000,
  headers: {},
});

// Attach JWT token to every request
reviewsClient.interceptors.request.use(
  (cfg) => {
    const token = localStorage.getItem('token');
    if (token) {
      cfg.headers.Authorization = `Bearer ${token}`;
    }
    return cfg;
  },
  (error) => Promise.reject(error)
);

// On 401, attempt a token refresh and retry once before logging out
applyAuthRetryInterceptor(reviewsClient);

applyTokenRefreshInterceptor(reviewsClient);

export const reviewsService = {
  /**
   * Fetch metadata for all tracked companies.
   * @returns {Promise<Array>} List of { id, name, stock_ticker } objects.
   */
  getCompanies: async () => {
    const response = await reviewsClient.get('/reviews/companies');
    return response.data;
  },

  /**
   * Fetch aggregated dashboard summary metrics for a company.
   * @param {string} companyId - Internal company id, e.g. 'upstart'.
   * @returns {Promise<Object>} DashboardStats object.
   */
  getDashboard: async (companyId) => {
    const response = await reviewsClient.get(`/reviews/dashboard/${companyId}`);
    return response.data;
  },

  /**
   * Fetch the full daily time-series for charting.
   * @param {string} companyId - Internal company id.
   * @param {string|null} since - Optional ISO start date filter ('YYYY-MM-DD').
   * @param {string|null} until - Optional ISO end date filter ('YYYY-MM-DD').
   * @returns {Promise<Object>} TimeSeriesResponse with { company_id, records }.
   */
  getTimeSeries: async (companyId, since = null, until = null) => {
    const params = {};
    if (since) params.since = since;
    if (until) params.until = until;
    const response = await reviewsClient.get(
      `/reviews/timeseries/${companyId}`,
      { params }
    );
    return response.data;
  },

  /**
   * Persist an earnings ingest record for a ticker.
   * @param {string} ticker - Stock ticker, e.g. 'UPST'.
   * @param {string} periodEndDate - ISO date string for the period end.
   * @param {Object} sections - Keyed sections dict from the wizard.
   * @returns {Promise<Object>} Saved EarningsRecordOut.
   */
  /**
   * Persist an earnings ingest record for a company.
   * @param {string} companyId - Internal company id, e.g. 'upstart'.
   * @param {string} periodEndDate - ISO date string for the period end.
   * @param {Object} sections - Keyed sections dict from the wizard.
   * @returns {Promise<Object>} Saved EarningsRecordOut.
   */
  saveEarnings: async (companyId, periodEndDate, sections) => {
    const response = await reviewsClient.post(`/reviews/earnings/${companyId}`, {
      period_end_date: periodEndDate,
      sections,
    });
    return response.data;
  },

  /**
   * Fetch all earnings records for a company, newest first.
   * @param {string} companyId - Internal company id, e.g. 'upstart'.
   * @returns {Promise<Array>} List of EarningsRecordOut objects.
   */
  getEarnings: async (companyId) => {
    const response = await reviewsClient.get(`/reviews/earnings/${companyId}`);
    return response.data;
  },

  /**
   * Delete an earnings ingestion record by period end date.
   * @param {string} companyId - Internal company id, e.g. 'upstart'.
   * @param {string} periodEndDate - ISO date string matching the stored record.
   * @returns {Promise<void>}
   */
  deleteEarnings: async (companyId, periodEndDate) => {
    await reviewsClient.delete(
      `/reviews/earnings/${companyId}/${periodEndDate}`
    );
  },

  /**
   * Compare extracted earnings sections against records already in DynamoDB.
   * @param {string} companyId - Internal company id, e.g. 'UPST'.
   * @param {string} periodEndDate - Primary period end date hint.
   * @param {Object} sections - Keyed sections dict from the wizard.
   * @returns {Promise<Object>} EarningsCompareResponse.
   */
  compareEarnings: async (companyId, periodEndDate, sections) => {
    const response = await reviewsClient.post(`/reviews/earnings/${companyId}/compare`, {
      period_end_date: periodEndDate,
      sections,
    });
    return response.data;
  },

  // ---------------------------------------------------------------------------
  // Custom chart configuration
  // ---------------------------------------------------------------------------

  /**
   * List all saved custom chart configs for a company.
   * @param {string} companyTicker - Ticker symbol, e.g. 'UPST'.
   * @returns {Promise<Array>} List of CustomChartOut objects.
   */
  listCharts: async (companyTicker) => {
    const response = await reviewsClient.get(`/reviews/charts/${companyTicker}`);
    return response.data;
  },

  /**
   * Save a new custom chart configuration.
   * @param {string} companyTicker - Ticker symbol.
   * @param {string} name - Human-readable chart name.
   * @param {Object} config - Full chart state object.
   * @returns {Promise<Object>} The created CustomChartOut record.
   */
  createChart: async (companyTicker, name, config) => {
    const response = await reviewsClient.post(`/reviews/charts/${companyTicker}`, { name, config });
    return response.data;
  },

  /**
   * Update an existing saved custom chart.
   * @param {string} companyTicker - Ticker symbol.
   * @param {string} chartId - UUID of the chart to update.
   * @param {string} name - Updated name.
   * @param {Object} config - Updated full chart state.
   * @returns {Promise<Object>} The updated CustomChartOut record.
   */
  updateChart: async (companyTicker, chartId, name, config) => {
    const response = await reviewsClient.put(`/reviews/charts/${companyTicker}/${chartId}`, { name, config });
    return response.data;
  },

  /**
   * Delete a saved custom chart.
   * @param {string} companyTicker - Ticker symbol.
   * @param {string} chartId - UUID of the chart to delete.
   * @returns {Promise<void>}
   */
  deleteChart: async (companyTicker, chartId) => {
    await reviewsClient.delete(`/reviews/charts/${companyTicker}/${chartId}`);
  },
};

export default reviewsService;
